import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { X, Trash2 } from 'lucide-react'

type Props = {
  id:          string
  name:        string
  isLowStock?: boolean
  notes?:      string | null
  onClose:     () => void
  onRemove:    () => void
}

function renderItalicTail(name: string): React.ReactNode {
  const parts = name.trim().split(/\s+/)
  if (parts.length <= 1) return <em className="italic">{name}</em>
  const head = parts.slice(0, -1).join(' ')
  const tail = parts[parts.length - 1]
  return (
    <>
      {head} <em className="italic">{tail}</em>
    </>
  )
}

export function ShoppingListItemDetailSheet({
  name,
  isLowStock,
  notes,
  onClose,
  onRemove,
}: Props) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="list-item-sheet-title"
      className="fixed inset-0 z-50 flex items-end justify-center"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
      />

      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        className="relative w-full max-w-[460px] rounded-t-[24px] bg-cream pb-8 pt-3"
      >
        {/* Drag handle */}
        <div className="flex justify-center pb-3">
          <div className="h-1 w-10 rounded-full bg-border-avatar" />
        </div>

        {/* Masthead */}
        <div className="px-5 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                List item
              </div>
              <h2
                id="list-item-sheet-title"
                className="font-serif text-[22px] font-normal leading-[1.1] tracking-[-0.015em] text-ink break-words"
              >
                {renderItalicTail(name)}
              </h2>
              {isLowStock && (
                <p className="font-serif text-[12px] italic text-terracotta mt-1">
                  Running out.
                </p>
              )}
              {notes && (
                <p className="font-serif text-[12px] italic text-ink-muted mt-1">
                  {notes}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex-shrink-0 h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
            >
              <X className="h-[14px] w-[14px]" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Remove action */}
        <div className="px-5">
          <button
            type="button"
            onClick={onRemove}
            className="w-full rounded-[14px] border border-border-sand bg-paper py-3 px-4 flex items-center justify-center gap-2 text-critical transition active:bg-critical-bg"
          >
            <Trash2 className="h-[14px] w-[14px]" strokeWidth={1.8} />
            <span className="font-serif text-[14px] italic font-medium">
              Remove from list
            </span>
          </button>
        </div>
      </motion.div>
    </div>
  )
}
